import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import usePrize from "../hooks/usePrize";
import MetaTags from "../components/meta-tags";
import Header from "../components/Header/Header";
import Main from "../components/Main/Main";
import CtaButtonLayout from "../components/CtaButtonLayout/CtaButtonLayout";
import PageLoader from "../components/PageLoader/PageLoader";
import Footer from "../components/Footer/Footer";
import useHomePageData from "../hooks/useHomePageData";
import useWidget from "../hooks/useWidget";
import useStackAdaptPixel from "../hooks/useStackAdaptPixel";
import useIntentLyTag from "../hooks/useIntentLyTag";
import tracking from "../helpers/tracking";
import { useTwitterHeadScript } from "../hooks/useTwitterHeadScript";
import { getCharityData } from "../helpers/getCharityData";
import { getBonusDraw } from "../services/bonusDraw";
import { getGeneralContent } from "../services/generalContent";
import ReactGA4 from "react-ga4";

function App() {
  useStackAdaptPixel();
  useTwitterHeadScript();
  useIntentLyTag();

  const location = useLocation();
  const {
    raffle,
    prizeId,
    prizeLoading,
    raffles,
    isExpired,
    activeRaffles,
    subscriptionModels,
  } = usePrize();
  const {
    reviews,
    winners,
    isLoading: isHomePageDataloading,
  } = useHomePageData();
  const { widget: timelineWidget, isWidgetLoading: isTimelineWidgetLoading } =
    useWidget({ type: "timeline-logo-rotation-text" });
  const { widget: footerWidget, isWidgetLoading: isFooterWidgetLoading } =
    useWidget({ type: "footer-logo-rotation-text" });
  const [bonusDraw, setBonusDraw] = useState(null);
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getActiveBonusDraw();
    getPageContent();
    if (process.env.NODE_ENV === "production") {
      ReactGA4.initialize([
        { trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS4_TRACKING_ID },
        {
          trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS4_TRACKING_ID2,
          gtagName: "mainGtag2",
        },
      ]);
    } else {
      ReactGA4.initialize([
        { trackingId: process.env.REACT_APP_GOOGLE_ANALYTICS4_TRACKING_ID },
      ]);
    }
  }, []);

  useEffect(() => {
    tracking.twitterPageViewTrack(location.pathname);
    ReactGA4.send({ hitType: 'pageview', page: location.pathname, title: process.env.REACT_APP_CHARITY_NAME });
  }, [location]);

  useEffect(() => {
    setIsLoading(
      prizeLoading ||
        isHomePageDataloading ||
        isTimelineWidgetLoading ||
        isFooterWidgetLoading ||
        !content.dreamHome
    );
  }, [
    prizeLoading,
    isHomePageDataloading,
    isTimelineWidgetLoading,
    isFooterWidgetLoading,
    content,
  ]);

  const getActiveBonusDraw = async () => {
    const activeBonusDraw = await getBonusDraw();
    if (activeBonusDraw && activeBonusDraw.length) {
      setBonusDraw(activeBonusDraw[0]);
    }
  };

  const getPageContent = async () => {
    const pageContent = await getGeneralContent();
    if (pageContent.dreamHome) {
      setContent(pageContent);
    }
  };

  const charityTag = getCharityData(process.env.REACT_APP_CHARITY_NAME).linkTag;

  const onCTAClick = () => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(
        process.env.REACT_APP_BASE_URL +
          `/ticket-selector?charity=${charityTag}`,
        "_self"
      );
    }
  };
  const onLearnMoreClick = () => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(process.env.REACT_APP_BASE_URL + "/how-it-works");
    }
  };
  const onLearnMoreCharityClick = () => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(process.env.REACT_APP_BASE_URL + "/charity-raffle");
    }
  };
  const onLearnMoreBonusDrawClick = () => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(process.env.REACT_APP_BASE_URL + "/early-bird");
    }
  };
  const onMoreWinnersClick = () => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(process.env.REACT_APP_BASE_URL + "/winners");
    }
  };
  const onTwo4One = () => {
    if (process.env.REACT_APP_BASE_URL) {
      window.open(process.env.REACT_APP_BASE_URL + "/2-4-1");
    }
  };

  return (
    <>
      <MetaTags />
      {isLoading ? (
        <CtaButtonLayout isDark>
          <Header raffle={raffle} bonusDraw={bonusDraw} />
          <PageLoader loading />
          <Footer
            insideComponent={true}
            widget={footerWidget}
            bonusDraw={bonusDraw}
          />
        </CtaButtonLayout>
      ) : (
        <Main
          prize={null}
          prizeId={prizeId}
          raffle={raffle}
          raffles={raffles}
          subscriptionModels={subscriptionModels}
          activeRaffles={activeRaffles}
          isExpired={isExpired}
          reviews={reviews}
          winners={winners}
          onCTAClick={onCTAClick}
          onLearnMoreClick={onLearnMoreClick}
          onLearnMoreCharityClick={onLearnMoreCharityClick}
          onLearnMoreBonusDrawClick={onLearnMoreBonusDrawClick}
          onMoreWinnersClick={onMoreWinnersClick}
          onTwo4One={onTwo4One}
          widgets={{ timelineWidget, footerWidget }}
          bonusDraw={bonusDraw}
          content={content}
        />
      )}
      <ToastContainer />
    </>
  );
}

export default App;
